import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { MAIN_THEME } from '@styles';
import { cloneDeep } from 'lodash';
import clsx from 'clsx';
import { IRepeaterBoxFormFieldProps } from '.';
import RepeaterBoxInputField from './RepeaterBoxInputField';
import { useResetKey } from '@hooks/useResetKey';

const useStyles = makeStyles()(() => ({
  container: {
    borderRadius: '4px',
    padding: '0 6.5px 10px 6.5px',
    position: 'relative',
    margin: '0 -6.5px',
  },
  multiContainer: {
    paddingTop: '20px',
    backgroundColor: MAIN_THEME.color.white98,
  },
  title: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.bold,
    marginBottom: '10px',
  },
  closedButton: {
    position: 'absolute',
    right: '10px',
    top: '5px',
    width: 'fit-content',
    border: 0,
    backgroundColor: 'transparent',
    padding: 0,
    color: MAIN_THEME.color.white65,
    fontSize: MAIN_THEME.fontSize.rg,
    fontFamily: MAIN_THEME.font.regular,
    marginRight: '3px',
    cursor: 'pointer',
    '&:hover:not(disabled)': {
      color: MAIN_THEME.color.red,
    },
  },
  rowContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  addButton: {
    border: 0,
    backgroundColor: 'transparent',
    padding: 0,
    color: 'rgba(211, 13, 43, 0.8)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: MAIN_THEME.font.regular,
    fontSize: MAIN_THEME.fontSize.sm,
    textDecoration: 'underline',

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
}));

const RepeaterBoxFormField: React.FC<IRepeaterBoxFormFieldProps> = ({
  title,
  values,
  inputs,
  addButtonText,
  loadingFields,
  dynamicAddButtonText,
  minRepeaterLength,
  maxRepeaterLength,
  onChange,
  onBlur,
  onAutocompleteSearch,
  error = [],
  touched = [],
  onAddBox,
  onDeleteBox,
  disabled,
}) => {
  const { classes } = useStyles();
  const canAdd = values?.length < maxRepeaterLength;
  const canDelete = values?.length > minRepeaterLength;
  const [autocompleteKey, rerenderAutocomplete] =
    useResetKey('autocompleteKey');

  const addNewRecord = () => {
    {
      if (values.length >= maxRepeaterLength) return;
      const newValues = cloneDeep(values);
      newValues.push(
        Object.keys(inputs).reduce<Record<string, string>>((acc, key) => {
          acc[key] = '';
          return acc;
        }, {})
      );
      onChange(newValues);
      onAddBox && onAddBox();
    }
  };

  const removeRecord = (index: number) => {
    const newValues = cloneDeep(values);
    newValues.splice(index, 1);
    onDeleteBox && onDeleteBox(index);
    onChange(newValues);
    rerenderAutocomplete();
  };

  const onFieldChange = (
    inputName: string,
    value: string,
    rowIndex: number
  ) => {
    const newValues = cloneDeep(values);
    newValues[rowIndex][inputName] = value;
    onChange(newValues, inputName);
  };

  const onFieldBlur = (inputName: string, rowIndex: number) => {
    onBlur && onBlur(inputName, rowIndex);
  };

  return (
    <div>
      {title && <div className={classes.title}>{title}</div>}
      <div className={classes.rowContainer}>
        {values.map((row, rowIndex) => {
          const rowValues = values[rowIndex] || {};
          const rowErrors = error[rowIndex] || {};
          const rowTouched = touched[rowIndex] || {};
          return (
            <>
              <div
                key={`values-${rowIndex}`}
                className={clsx(classes.container, [
                  values.length > 1 && classes.multiContainer,
                ])}
              >
                {canDelete && !disabled && (
                  <button
                    type="button"
                    className={classes.closedButton}
                    onClick={() => removeRecord(rowIndex)}
                  >
                    ✖︎
                  </button>
                )}
                {Object.values(inputs).map((input) => {
                  return (
                    <RepeaterBoxInputField
                      key={input.name}
                      autocompleteKey={autocompleteKey}
                      value={rowValues[input.name]}
                      rowIndex={rowIndex}
                      onFieldChange={onFieldChange}
                      onFieldBlur={onFieldBlur}
                      error={rowErrors[input.name]}
                      touched={rowTouched[input.name]}
                      onAutocompleteSearch={onAutocompleteSearch}
                      disabled={disabled}
                      loading={loadingFields?.[input.name]}
                      input={input}
                    />
                  );
                })}
              </div>
              {canAdd && rowIndex === values.length - 1 && (
                <button
                  type="button"
                  className={classes.addButton}
                  onClick={addNewRecord}
                  disabled={disabled}
                >
                  {dynamicAddButtonText?.length > 0 &&
                  rowIndex < dynamicAddButtonText.length
                    ? dynamicAddButtonText[rowIndex]
                    : addButtonText}
                </button>
              )}
            </>
          );
        })}
      </div>
    </div>
  );
};

export default RepeaterBoxFormField;
